/**
 * Fetch wrapper for the Laravel API. Same-origin in production (finpip.com),
 * proxied in dev via Vite. Sanctum SPA sessions use the XSRF-TOKEN cookie for
 * CSRF — we grab it before any mutating request.
 */

// The API lives at /journal/api and /journal/sanctum — same origin as the SPA.
const BASE = "/journal";

let csrfPrimed = false;

async function primeCsrf(): Promise<void> {
  if (csrfPrimed) return;
  await fetch(`${BASE}/sanctum/csrf-cookie`, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
  });
  csrfPrimed = true;
}

function readXsrfCookie(): string {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

export interface ApiError extends Error {
  status: number;
  body?: unknown;
}

function makeErr(status: number, message: string, body?: unknown): ApiError {
  const e = new Error(message) as ApiError;
  e.status = status;
  e.body = body;
  return e;
}

export interface ApiRequestOpts<TBody = unknown> {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: TBody;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  /** Skip CSRF priming (login/register only) */
  noCsrf?: boolean;
}

export async function api<TResp = unknown, TBody = unknown>(
  path: string,
  opts: ApiRequestOpts<TBody> = {},
): Promise<TResp> {
  const method = opts.method ?? (opts.body !== undefined ? "POST" : "GET");
  const url = new URL(`${BASE}${path.startsWith("/") ? path : `/${path}`}`, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
  }

  if (method !== "GET" && !opts.noCsrf) {
    await primeCsrf();
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET") {
    const xsrf = readXsrfCookie();
    if (xsrf) headers["X-XSRF-TOKEN"] = xsrf;
  }

  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method,
      credentials: "include",
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (e) {
    throw makeErr(0, (e as Error).message || "Network error");
  }

  const raw = await res.text();
  let parsed: unknown = null;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch {
    /* keep raw */
  }

  if (!res.ok) {
    const asObj = (parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {});
    const msg =
      (typeof asObj.message === "string" && asObj.message) ||
      (typeof asObj.error === "string" && asObj.error) ||
      raw ||
      `HTTP ${res.status}`;
    throw makeErr(res.status, msg, parsed);
  }
  return parsed as TResp;
}

// Named endpoint groups — expand per module.
export const authApi = {
  me: () =>
    api<{
      user: AuthUser | null;
      registration_enabled: boolean;
      needs_onboarding: boolean;
      email_verified: boolean;
    }>("/api/me"),
  register: (payload: { email: string; password: string; name?: string }) =>
    api<{ user: AuthUser; email_verified: boolean }>("/api/register", { method: "POST", body: payload }),
  login: (payload: { email: string; password: string }) =>
    api<{ user: AuthUser; email_verified: boolean }>("/api/login", { method: "POST", body: payload }),
  logout: () => api("/api/logout", { method: "POST" }),
  resendVerification: () => api<{ ok: boolean }>("/api/email/resend", { method: "POST" }),
  updateProfile: (payload: {
    name?: string;
    email?: string;
    password?: string;
    password_confirmation?: string;
  }) =>
    api<{ user: AuthUser; email_verified: boolean }>("/api/profile", {
      method: "PATCH",
      body: payload,
    }),
};

export const accountApi = {
  // Danger zone: wipe all trades + wallet entries for a clean slate.
  reset: () =>
    api<{ ok: boolean; trades_deleted: number; wallet_deleted: number; balance: number }>(
      "/api/account/reset",
      { method: "POST" },
    ),
};

export interface OnboardingPayload {
  display_timezone: string;
  mt5_import_timezone: string;
  goal_amount: number;
  start_balance?: number;
  level_step_pct?: number;
}

export const onboardingApi = {
  complete: (payload: OnboardingPayload) =>
    api<{ ok: boolean }>("/api/onboarding", { method: "POST", body: payload }),
};

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  is_admin?: boolean;
}

// ------- Admin (users + feedback management) -------

export interface AdminUserRow {
  id: number;
  name: string;
  email: string;
  email_verified_at: string | null;
  is_admin: boolean;
  trades_count: number;
  feedback_count: number;
  created_at: string;
}

export interface AdminUserDetail {
  user: {
    id: number;
    name: string;
    email: string;
    email_verified_at: string | null;
    is_admin: boolean;
    created_at: string;
    updated_at: string;
  };
  trades_count: number;
  feedback_count: number;
  balance: number;
  settings: {
    display_timezone: string;
    mt5_import_timezone: string;
    max_loss_pct: number | null;
    openai_model: string;
    onboarding_completed_at: string | null;
  } | null;
  target: {
    id: number;
    kind: string;
    goal_amount: number | null;
    start_balance: number;
    level_step_pct: number;
    completed_at: string | null;
  } | null;
}

export interface AdminStats {
  total_users: number;
  verified_users: number;
  admins: number;
  total_trades: number;
  feedback_count: number;
  new_feedback: number;
  signups_last_7_days: number;
}

export interface FeedbackItem {
  id: number;
  user_id: number | null;
  user_name: string;
  user_email: string;
  name: string | null;
  email: string | null;
  category: "bug" | "idea" | "other" | string;
  message: string;
  url: string | null;
  user_agent: string | null;
  status: "new" | "read" | "resolved";
  created_at: string;
  updated_at: string;
}

export interface PageMeta {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

export const adminApi = {
  stats: () => api<AdminStats>("/api/admin/stats"),
  users: (query: { search?: string; per_page?: number } = {}) =>
    api<{ users: AdminUserRow[]; meta: PageMeta }>("/api/admin/users", { query }),
  user: (id: number) => api<AdminUserDetail>(`/api/admin/users/${id}`),
  updateUser: (id: number, payload: { is_admin?: boolean; email_verified?: boolean }) =>
    api<{ user: AdminUserRow }>(`/api/admin/users/${id}`, { method: "PATCH", body: payload }),
  deleteUser: (id: number) => api<{ ok: boolean }>(`/api/admin/users/${id}`, { method: "DELETE" }),
  feedback: (query: { status?: string; per_page?: number } = {}) =>
    api<{ feedback: FeedbackItem[]; meta: PageMeta }>("/api/admin/feedback", { query }),
  updateFeedback: (id: number, status: "new" | "read" | "resolved") =>
    api<{ feedback: FeedbackItem }>(`/api/admin/feedback/${id}`, { method: "PATCH", body: { status } }),
  deleteFeedback: (id: number) =>
    api<{ ok: boolean }>(`/api/admin/feedback/${id}`, { method: "DELETE" }),
};

export const feedbackApi = {
  submit: (payload: { category?: string; message: string; url?: string; user_agent?: string }) =>
    api<{ feedback: FeedbackItem }>("/api/feedback", { method: "POST", body: payload }),
};

// ------- Wallet -------

export interface WalletEntry {
  id: number;
  type: "deposit" | "withdrawal";
  amount: number;
  happened_at: string;
  is_saving: boolean;
  notes: string | null;
  balance_after: number | null;
}

export interface WalletSummary {
  deposits_total: number;
  /** Withdrawals that were SPENT; `savings_total` is reported separately. */
  withdrawals_total: number;
  savings_total: number;
  net_deposited: number;
  /** Tracked balance — still counts withdrawals that were banked as savings. */
  balance: number;
  /** What is really left in the trading account: balance minus savings. */
  account_balance: number;
  entry_count: number;
}

// ------- Assistant -------

export interface AssistantMessage {
  id: number;
  role: "user" | "assistant" | "system";
  content: string;
  created_at: string | null;
}

export interface AssistantSettings {
  configured: boolean;
  model: string;
  coach_instructions: string | null;
}

export const assistantApi = {
  settings: () => api<AssistantSettings>("/api/assistant/settings"),
  saveSettings: (payload: {
    api_key?: string;
    model?: string;
    coach_instructions?: string;
    remove_key?: boolean;
  }) => api<{ ok: boolean }>("/api/assistant/settings", { method: "POST", body: payload }),
  history: () => api<{ messages: AssistantMessage[] }>("/api/assistant/messages"),
  chat: (message: string) =>
    api<{ message: AssistantMessage }>("/api/assistant/chat", { method: "POST", body: { message } }),
  newThread: () => api<{ ok: boolean }>("/api/assistant/new-thread", { method: "POST" }),
  briefing: () =>
    api<{ message: AssistantMessage }>("/api/assistant/briefing", { method: "POST" }),
};

// ------- Reports -------

export interface ReportHeadline {
  trade_count: number;
  win_count: number;
  loss_count: number;
  win_rate_pct: number;
  gross_profit: number;
  gross_loss: number;
  net_profit: number;
  avg_win: number;
  avg_loss: number;
  expectancy: number;
  profit_factor: number | null;
}

export interface SymbolBreakdown {
  symbol: string;
  trades: number;
  net_pl: number;
  win_rate_pct: number;
}

export interface DowBreakdown {
  dow: number;
  label: string;
  trades: number;
  net_pl: number;
}

export interface HourBreakdown {
  hour: number;
  trades: number;
  net_pl: number;
}

export interface MonthBreakdown {
  month: string;
  trades: number;
  net_pl: number;
}

export interface DayBreakdown {
  date: string;
  trades: number;
  net_pl: number;
}

export interface NewsBreakdown {
  bucket: "news" | "normal";
  trades: number;
  wins: number;
  losses: number;
  /** null when the bucket has no trades yet. */
  win_rate_pct: number | null;
  net_pl: number;
  avg_pl: number | null;
  profit_factor: number | null;
}

export const reportsApi = {
  overview: () =>
    api<{
      headline: ReportHeadline;
      by_symbol: SymbolBreakdown[];
      by_dow: DowBreakdown[];
      by_hour: HourBreakdown[];
      by_news: NewsBreakdown[];
      by_day: DayBreakdown[];
      monthly: MonthBreakdown[];
    }>("/api/reports/overview"),
};

// ------- MT5 Import -------

export type ImportPlatform = "mt5" | "mt4";

export interface Mt5ImportRecord {
  id: number;
  filename: string;
  platform: ImportPlatform;
  status: "queued" | "running" | "completed" | "failed";
  deals_seen: number;
  trades_inserted: number;
  trades_skipped: number;
  wallet_entries_inserted: number;
  error: string | null;
  finished_at: string | null;
  created_at: string | null;
}

export const mt5Api = {
  list: () => api<{ imports: Mt5ImportRecord[] }>("/api/mt5-imports"),
  upload: async (file: File, platform: ImportPlatform = "mt5") => {
    // multipart upload — bypass the JSON body flow.
    const form = new FormData();
    form.append("file", file);
    // The server validates the accepted extensions per platform (xlsx for MT5,
    // htm/html for MT4), so this has to travel with the file.
    form.append("platform", platform);
    await getCsrfCookie();
    const xsrf = readXsrfCookie();
    const res = await fetch("/journal/api/mt5-imports", {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-XSRF-TOKEN": xsrf,
      },
      body: form,
    });
    const raw = await res.text();
    let parsed: unknown = null;
    try {
      parsed = raw ? JSON.parse(raw) : null;
    } catch {
      /* keep raw */
    }
    if (!res.ok) {
      const asObj = (parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {});
      throw new Error(
        (typeof asObj.message === "string" && asObj.message) ||
          `Upload failed (HTTP ${res.status})`,
      );
    }
    return parsed as { import: Mt5ImportRecord };
  },
};

// Expose these helpers so mt5Api can reach the same CSRF flow.
async function getCsrfCookie(): Promise<void> {
  await primeCsrf();
}

// ------- Dashboard -------

export interface DashboardSummary {
  balance: number;
  /** Total withdrawn and banked rather than spent. 0 when nothing is saved. */
  saved_total: number;
  /** balance - saved_total: what is really left in the trading account. */
  account_balance: number;
  progress: {
    level: number;
    tier: "Bronze" | "Silver" | "Gold" | "Platinum" | "Diamond";
    start_balance: number;
    step_pct: number;
    current_level_balance: number;
    next_level_balance: number;
    level_progress_pct: number;
    /** Money still needed to complete the current level. */
    to_next_level: number;
    goal_amount: number | null;
    goal_progress_pct: number | null;
  };
  /** The current calendar week, bucketed exactly like the calendar page. */
  week: {
    start: string;
    timezone: string;
    days: CalendarDay[];
  };
  stats: {
    today_pl: number;
    today_trades: number;
    week_pl: number;
    week_trades: number;
    month_pl: number;
    month_trades: number;
    total_trades: number;
    win_rate_pct: number;
  };
  has_target: boolean;
  activity: Array<
    | {
        kind: "trade";
        ts: string;
        trade: {
          id: number;
          side: "Buy" | "Sell";
          symbol: string | null;
          status: "open" | "closed";
          profit: number;
          net_pl: number;
          balance_after: number | null;
        };
      }
    | {
        kind: "wallet";
        ts: string;
        wallet: {
          id: number;
          type: "deposit" | "withdrawal";
          amount: number;
          is_saving: boolean;
          balance_after: number | null;
        };
      }
  >;
}

export const dashboardApi = {
  summary: () => api<DashboardSummary>("/api/dashboard/summary"),
};

// ------- Journey -------

export interface JourneyTarget {
  id: number;
  kind: "absolute" | "daily_pct";
  goal_amount: number;
  start_balance: number;
  level_step_pct: number;
  active_from: string | null;
  completed_at: string | null;
}

export interface JourneyProgress {
  balance: number;
  start_balance: number;
  level: number;
  tier: "Bronze" | "Silver" | "Gold" | "Platinum" | "Diamond";
  current_level_balance: number;
  next_level_balance: number;
  level_progress_pct: number;
  /** Money still needed to complete the current level. */
  to_next_level: number;
  goal_amount: number | null;
  goal_progress_pct: number | null;
  step_pct: number;
  streak_days: number;
  days_since_level_up: number | null;
  eta_days_to_goal: number | null;
  avg_daily_pl_30d: number | null;
}

export interface CompoundRow {
  level: number;
  balance: number;
  level_gain: number;
  tier: string;
}

export const journeyApi = {
  show: () =>
    api<{ target: JourneyTarget | null; progress: JourneyProgress; compound_table: CompoundRow[] }>(
      "/api/journey",
    ),
  saveTarget: (payload: { goal_amount: number; level_step_pct?: number; start_balance?: number }) =>
    api<{ ok: boolean; target: JourneyTarget }>("/api/journey/target", { method: "POST", body: payload }),
};

// ------- EA auto-sync (API keys) -------

export interface ApiKeyRecord {
  id: number;
  name: string;
  last_four: string;
  last_used_at: string | null;
  created_at: string | null;
}

export const apiKeysApi = {
  list: () => api<{ keys: ApiKeyRecord[] }>("/api/api-keys"),
  /** The plaintext key comes back ONCE here and can never be re-read. */
  create: () =>
    api<{ key: ApiKeyRecord; plain: string }>("/api/api-keys", { method: "POST", body: {} }),
  destroy: (id: number) => api<{ ok: boolean }>(`/api/api-keys/${id}`, { method: "DELETE" }),
  /**
   * Compiled build (.ex5/.ex4) by default; `source` gets the readable .mq5/.mq4.
   * `service` (MT5 only) gets the Service build, which needs no chart — for
   * users already running an EA, since MT5 allows one EA per chart.
   */
  downloadUrl: (platform: "mt4" | "mt5", source = false, service = false) => {
    const q = [source ? "source=1" : "", service ? "service=1" : ""].filter(Boolean).join("&");
    return `/journal/api/ea/download/${platform}${q ? `?${q}` : ""}`;
  },
};

// ------- Calendar -------

export interface CalendarDay {
  date: string;
  net_pl: number;
  trades: number;
  wins: number;
  losses: number;
  deposits: number;
  /** Withdrawals that were SPENT — savings are reported separately below. */
  withdrawals: number;
  /** Withdrawals banked as savings. */
  savings: number;
}

export interface CalendarMonth {
  month: string;
  timezone: string;
  /** 0 = Sunday .. 6 = Saturday, for padding the leading blanks. */
  first_weekday: number;
  days_in_month: number;
  days: CalendarDay[];
  totals: {
    net_pl: number;
    trades: number;
    wins: number;
    losses: number;
    deposits: number;
    withdrawals: number;
    savings: number;
    green_days: number;
    red_days: number;
  };
}

export const calendarApi = {
  show: (month?: string) =>
    api<CalendarMonth>("/api/calendar", { query: month ? { month } : {} }),
};

// ------- Trades -------

export interface Trade {
  id: number;
  side: "Buy" | "Sell";
  symbol: string | null;
  asset_class: string | null;
  volume: number | null;
  open_time: string | null;
  close_time: string | null;
  open_price: number | null;
  close_price: number | null;
  sl: number | null;
  tp: number | null;
  sl_hit: boolean;
  tp_hit: boolean;
  /** Was this trade taken around a news release? */
  news_trade: boolean;
  profit: number;
  commission: number;
  swap: number;
  net_pl: number;
  balance_after: number | null;
  status: "open" | "closed";
  notes: string | null;
  stars: number | null;
  size_class: "low" | "normal" | "over" | "unknown";
  source: "manual" | "mt5_import" | "mt4_import";
  level_before: number | null;
  level_after: number | null;
  /** How many trades were absorbed into this one; 0 = not a merged trade. */
  merged_from_count: number;
}

export interface TradesFilter {
  status?: "open" | "closed" | "all";
  symbol?: string;
  side?: "Buy" | "Sell";
  pnl?: "profit" | "loss" | "any";
  search?: string;
  sort?: "newest" | "oldest" | "biggest_win" | "biggest_loss";
  per_page?: number;
}

export interface TradeDetail extends Trade {
  strategy_ids: number[];
  factors: Array<{ id: number; value: "yes" | "no" }>;
}

export const tradesApi = {
  list: (filter: TradesFilter = {}) =>
    api<{ trades: Trade[]; meta: { total: number; per_page: number; current_page: number; last_page: number } }>(
      "/api/trades",
      { query: filter as Record<string, string | number | undefined> },
    ),
  get: (id: number) => api<{ trade: TradeDetail }>(`/api/trades/${id}`),
  create: (payload: Partial<Trade>) =>
    api<{ trade: Trade }>("/api/trades", { method: "POST", body: payload }),
  update: (id: number, payload: Partial<Trade>) =>
    api<{ trade: Trade }>(`/api/trades/${id}`, { method: "PATCH", body: payload }),
  destroy: (id: number) => api<{ ok: boolean }>(`/api/trades/${id}`, { method: "DELETE" }),
  merge: (tradeIds: number[]) =>
    api<{ ok: boolean; trade: Trade }>("/api/trades/merge", {
      method: "POST",
      body: { trade_ids: tradeIds },
    }),
  unmerge: (id: number) =>
    api<{ ok: boolean; restored: number }>(`/api/trades/${id}/unmerge`, { method: "POST" }),
  setStrategies: (id: number, strategyIds: number[]) =>
    api<{ trade: TradeDetail }>(`/api/trades/${id}/strategies`, {
      method: "POST",
      body: { strategy_ids: strategyIds },
    }),
  setFactor: (id: number, factorId: number, value: "yes" | "no" | null) =>
    api<{ trade: TradeDetail }>(`/api/trades/${id}/factor`, {
      method: "POST",
      body: { factor_id: factorId, value },
    }),
};

// ------- Strategies & Factors -------

export interface Strategy {
  id: number;
  name: string;
  description: string | null;
  status: "active" | "archived";
}

export interface FactorRecord {
  id: number;
  label: string;
  kind: "positive" | "negative";
  sort_order: number;
}

export const strategiesApi = {
  list: () => api<{ strategies: Strategy[] }>("/api/strategies"),
  create: (payload: { name: string; description?: string }) =>
    api<{ strategy: Strategy }>("/api/strategies", { method: "POST", body: payload }),
  update: (id: number, payload: Partial<Strategy>) =>
    api<{ strategy: Strategy }>(`/api/strategies/${id}`, { method: "PATCH", body: payload }),
  destroy: (id: number) => api<{ ok: boolean }>(`/api/strategies/${id}`, { method: "DELETE" }),
};

export const factorsApi = {
  list: () => api<{ factors: FactorRecord[] }>("/api/factors"),
  create: (payload: { label: string; kind: "positive" | "negative" }) =>
    api<{ factor: FactorRecord }>("/api/factors", { method: "POST", body: payload }),
  update: (id: number, payload: Partial<FactorRecord>) =>
    api<{ factor: FactorRecord }>(`/api/factors/${id}`, { method: "PATCH", body: payload }),
  destroy: (id: number) => api<{ ok: boolean }>(`/api/factors/${id}`, { method: "DELETE" }),
};

export interface WalletAnalytics {
  series: Array<{
    ts: string;
    balance: number;
    cum_deposits: number;
    cum_withdrawals: number;
    cum_savings: number;
    net_deposited: number;
    cum_trade_pl: number;
  }>;
  summary: {
    balance: number;
    cum_deposits: number;
    cum_withdrawals: number;
    cum_savings: number;
    net_deposited: number;
    cum_trade_pl: number;
    real_return: number;
    roi_pct: number | null;
  };
  monthly: Array<{ month: string; deposits: number; withdrawals: number; savings: number }>;
}

// ------- Public shareable stats (growth loop) -------

export interface PublicStatsHeadline {
  trade_count: number;
  win_rate_pct: number;
  profit_factor: number | null;
  total_return_pct: number;
}

export interface PublicStats {
  display_name: string;
  headline: PublicStatsHeadline;
  // Time-weighted performance index (starts at 100). No dollars — privacy fix.
  series: { ts: string; index: number }[];
}

export const publicShareApi = {
  // Public, unauthenticated read — plain GET, no CSRF.
  stats: (token: string) => api<PublicStats>(`/api/s/${encodeURIComponent(token)}`),
  enable: () =>
    api<{ share_public: boolean; share_token: string; share_url: string }>("/api/share/enable", {
      method: "POST",
    }),
  disable: () => api<{ share_public: boolean }>("/api/share/disable", { method: "POST" }),
};

export const walletApi = {
  list: () => api<{ entries: WalletEntry[]; balance: number }>("/api/wallet"),
  summary: () => api<WalletSummary>("/api/wallet/summary"),
  analytics: () => api<WalletAnalytics>("/api/wallet/analytics"),
  create: (payload: {
    type: "deposit" | "withdrawal";
    amount: number;
    happened_at: string;
    is_saving?: boolean;
    notes?: string;
  }) => api<{ entry: WalletEntry }>("/api/wallet", { method: "POST", body: payload }),
  update: (id: number, payload: Partial<WalletEntry>) =>
    api<{ entry: WalletEntry }>(`/api/wallet/${id}`, { method: "PATCH", body: payload }),
  destroy: (id: number) => api<{ ok: boolean }>(`/api/wallet/${id}`, { method: "DELETE" }),
  recalculate: () =>
    api<{ ok: boolean; balance: number }>("/api/wallet/recalculate", { method: "POST" }),
};
